import { CONFIRMED_MARK, outreachKind } from './outreachEvidence.js';
// Mi espacio › Hoy: the three suggested priorities. An explicit rule, in code
// (no AI): each suggestion says why it is there and which record it opens.
// Ale accepts them, changes them or picks others; nothing is saved until she
// does. Personal items come from the day view (her tasks and goal actions);
// commercial follow-ups come from the commercial records already loaded in the
// browser (src/followupQueue.js). Nothing is copied: a commercial priority keeps
// only the contact's id.
//
// Order (higher first). Commercial follow-ups take at most 2 of the 3 places,
// so her own goals are never crowded out when there are open ones.
//   100  a contact answered and is waiting (the fresher, the higher)
//    90  a task or action of mine overdue
//    85  a high-priority task for the day
//    80  urgent commercial follow-up (3–7 days since the last touch)
//    72  a task of the day linked to an active goal
//    68  an action of a goal planned for the day
//    60  commercial follow-up (8–21 days)
//    50  any other open task of the day
//    40  a high-priority task due in the next 2 days
export const SUGGESTION_RULES = {
  max: 3, maxCommercial: 2,
  reply: 100, overdue: 90, highToday: 85, urgentFollowup: 80, goalTask: 72, goalAction: 68, followup: 60, today: 50, soonHigh: 40,
};

const addDays = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const fmt = (iso) => { try { return new Date(`${iso}T12:00:00Z`).toLocaleDateString('es', { day: 'numeric', month: 'short', timeZone: 'UTC' }); } catch { return iso; } };
const ago = (n) => (n <= 0 ? 'hoy' : n === 1 ? 'ayer' : `hace ${n} días`);

/**
 * day: the server's day view (tasks, overdue, upcoming, actions, priorities, goals, today, date).
 * replies / followups: [{ id, name, accountId, accountName, daysSince, tag }] (already filtered: no
 * contact dismissed today, none contacted today).
 * Returns up to three { kind, id, title, reason, score, open } — `open` says what to open.
 */
export function suggestPriorities({ day, replies = [], followups = [], rules = SUGGESTION_RULES }) {
  if (!day) return [];
  const goals = new Map((day.goals || []).map(g => [g.id, g]));
  const activeGoal = (ids) => (ids || []).map(id => goals.get(id)).find(g => g && g.status === 'active');
  const c = [];
  const push = (x) => { if (!c.some(y => y.kind === x.kind && y.id === x.id)) c.push(x); };

  for (const r of replies) push({ kind: 'contact', id: r.id, title: `Responder a ${r.name}`, reason: `Te escribió ${ago(r.daysSince)}${r.accountName ? ` · ${r.accountName}` : ''}`, score: rules.reply - Math.min(r.daysSince || 0, 9), commercial: true, open: { type: 'contact', id: r.id, accountId: r.accountId || null } });
  for (const o of day.overdue || []) {
    if (o.type === 'task') push({ kind: 'task', id: o.id, title: o.title, reason: `Vencida desde el ${fmt(o.dueOn)}`, score: rules.overdue, open: { type: 'task', id: o.id } });
    else push({ kind: 'application', id: o.id, title: o.idea, reason: `Acción planificada para el ${fmt(o.plannedOn)}, sin hacer`, score: rules.overdue - 1, open: { type: 'application', id: o.id } });
  }
  const isToday = day.date === day.today;
  const openToday = (day.tasks || []).filter(t => t.status === 'open');
  for (const t of openToday) {
    const g = activeGoal(t.goalIds);
    if (t.priority === 'high') push({ kind: 'task', id: t.id, title: t.title, reason: `Prioridad alta para ${isToday ? 'hoy' : 'este día'}${g ? ` · objetivo «${g.title}»` : ''}`, score: rules.highToday, open: { type: 'task', id: t.id } });
    else if (g) push({ kind: 'task', id: t.id, title: t.title, reason: `Avanza tu objetivo «${g.title}»`, score: rules.goalTask, open: { type: 'task', id: t.id } });
  }
  for (const f of followups) {
    const urgent = f.tag === 'urgent';
    if (!urgent && f.tag !== 'followup') continue;
    push({ kind: 'contact', id: f.id, title: `Seguimiento a ${f.name}`, reason: `Último contacto ${ago(f.daysSince)}${f.accountName ? ` · ${f.accountName}` : ''}`, score: urgent ? rules.urgentFollowup : rules.followup, commercial: true, open: { type: 'contact', id: f.id, accountId: f.accountId || null } });
  }
  for (const a of (day.actions || []).filter(a => a.status !== 'done')) {
    const g = a.goalId ? goals.get(a.goalId) : null;
    push({ kind: 'application', id: a.id, title: a.idea, reason: g ? `Acción de tu objetivo «${g.title}»` : 'Acción planificada para hoy', score: rules.goalAction, open: { type: 'application', id: a.id } });
  }
  for (const t of openToday) push({ kind: 'task', id: t.id, title: t.title, reason: `Tarea de ${isToday ? 'hoy' : 'este día'}`, score: rules.today, open: { type: 'task', id: t.id } });
  const soon = addDays(day.date, 2);
  for (const t of (day.upcoming || []).filter(t => t.priority === 'high' && t.dueOn <= soon)) push({ kind: 'task', id: t.id, title: t.title, reason: `Prioridad alta, vence el ${fmt(t.dueOn)}`, score: rules.soonHigh, open: { type: 'task', id: t.id } });

  c.sort((a, b) => b.score - a.score);
  const out = [];
  let commercial = 0;
  for (const x of c) {
    if (out.length >= rules.max) break;
    if (x.commercial && commercial >= rules.maxCommercial) continue;
    if (x.commercial) commercial++;
    out.push(x);
  }
  return out;
}

/**
 * Commercial follow-ups (src/followupQueue.js results) → the plain shape suggestPriorities reads.
 * A reply is pending while the latest interaction is the contact's; a follow-up leaves the queue
 * once the contact is touched today (followupQueue already skips those).
 */
export function plainFollowups({ replies = [], focus = [], accounts = [], dismissedReplies = new Set(), dismissedFollowups = new Set() }) {
  const nameOf = (s) => `${s.fields?.['Name'] || ''} ${s.fields?.['Last name'] || ''}`.trim() || 'contacto';
  const accOf = (s) => { const id = (s.fields?.['Account'] || [])[0] || null; return { accountId: id, accountName: id ? accounts.find(a => a.id === id)?.fields?.['Account Name'] || '' : '' }; };
  return {
    replies: replies.filter(r => !dismissedReplies.has(r.s.id)).map(r => ({ id: r.s.id, name: nameOf(r.s), daysSince: r.daysSince, ...accOf(r.s) })),
    followups: focus.filter(f => !dismissedFollowups.has(f.s.id)).map(f => ({ id: f.s.id, name: nameOf(f.s), daysSince: f.e?.daysSince ?? null, tag: f.tag, ...accOf(f.s) })),
  };
}

/** The local day (YYYY-MM-DD) of an instant in a time zone. */
export function localDayOf(iso, tz) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  try { return new Intl.DateTimeFormat('en-CA', { timeZone: tz || 'UTC', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d); }
  catch { return d.toISOString().slice(0, 10); }
}

export { CONFIRMED_MARK };

/**
 * Is a commercial priority done? Only with evidence, never because a channel was opened:
 * - `confirmed`: Ale said "Lo contacté" on the priority (moment and channel kept in Mi espacio);
 * - `gmail`: the server found in HER Gmail an outgoing message (label SENT) addressed to this
 *   contact and sent that day in her time zone (`verifySentMail`; a [gmsg:…] mark alone is not
 *   enough, synced received messages carry it too);
 * - `logged`: the activity logged from Hoy right after her confirmation (CONFIRMED_MARK).
 * An activity logged when Gmail, WhatsApp or LinkedIn was opened is only `unconfirmed`: a hint.
 * A Gmail record of that day not verified yet is `checking`.
 */
export function contactEvidence({ stakeholderId, outreach = [], date, tz, confirmedAt = null, gmail = null }) {
  if (confirmedAt) return { done: true, state: 'confirmed' };
  if (gmail?.verified) return { done: true, state: 'gmail', sentAt: gmail.sentAt || null };
  const ofDay = outreach.filter(o => (o.fields?.['Stakeholder'] || []).includes(stakeholderId) && o.fields?.['Date'] && localDayOf(o.fields['Date'], tz) === date);
  if (ofDay.some(o => outreachKind(o) === 'confirmed')) return { done: true, state: 'logged' };
  if (!gmail && ofDay.some(o => outreachKind(o) === 'gmail_sent')) return { done: false, state: 'checking' };
  if (ofDay.some(o => outreachKind(o) === 'opened')) return { done: false, state: 'unconfirmed' };
  return { done: false, state: 'none' };
}
